/**
 * The person page's notes box and the tasks linked to them.
 */
import { useEffect, useState } from "react";
import { ListTodo, Plus, StickyNote } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button, Input, Textarea, useToast } from "@/components/ui";
import { Empty, Section, TickButton, dueLabel } from "@/features/command/ui";
import { TONE_CLASS, memberName } from "./shared";
import type { AffiliateTask } from "./types";

export function NotesCard({ notes, onSave }: { notes: string | null; onSave: (notes: string | null) => Promise<void> }) {
  const [text, setText] = useState(notes ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setText(notes ?? ""); }, [notes]);
  const dirty = text.trim() !== (notes ?? "").trim();

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await onSave(text.trim() || null);
      setSaved(true);
      setTimeout(() => setSaved(false), 1600);
    } catch { /* the page shows the error */ } finally { setSaving(false); }
  };

  return (
    <Section
      title="Notes"
      icon={StickyNote}
      action={saved ? <span className="text-[11px] text-emerald-300">Saved</span> : dirty ? <span className="text-[11px] text-zinc-500">Not saved yet</span> : undefined}
    >
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void save(); } }}
        rows={5}
        placeholder="How you met, what they're good at, what they asked for, what you promised"
        className="text-[13px] leading-relaxed"
      />
      {dirty && (
        <div className="mt-2 flex justify-end">
          <Button size="sm" loading={saving} onClick={() => void save()}>Save notes</Button>
        </div>
      )}
    </Section>
  );
}

export function TasksCard({
  affiliateId, tasks, onChanged,
}: {
  affiliateId: string;
  tasks: AffiliateTask[];
  onChanged: () => void;
}) {
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [local, setLocal] = useState<AffiliateTask[]>(tasks);
  useEffect(() => { setLocal(tasks); }, [tasks]);

  const add = async () => {
    const t = title.trim();
    if (!t) return;
    setAdding(true);
    try {
      await api.post("/tasks", { title: t, affiliate_id: affiliateId });
      setTitle("");
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add the task");
    } finally { setAdding(false); }
  };

  const toggle = async (task: AffiliateTask) => {
    const next = task.status === "completed" ? "pending" : "completed";
    setLocal((l) => l.map((x) => (x.id === task.id ? { ...x, status: next } : x)));
    try {
      await api.patch(`/tasks/${task.id}`, { status: next });
      onChanged();
    } catch (err) {
      setLocal((l) => l.map((x) => (x.id === task.id ? task : x)));
      toast.error(err instanceof ApiError ? err.message : "Couldn't update the task");
    }
  };

  const open = local.filter((t) => t.status !== "completed" && t.status !== "cancelled");
  const done = local.filter((t) => t.status === "completed");

  return (
    <Section title="Tasks" icon={ListTodo} count={open.length}>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); void add(); }}>
        <div className="min-w-0 flex-1">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a task, e.g. Send her the new clips" className="h-8 text-[13px]" />
        </div>
        <Button type="submit" size="sm" loading={adding} disabled={!title.trim()} leftIcon={<Plus className="h-3.5 w-3.5" />}>Add</Button>
      </form>
      {local.length === 0 ? (
        <Empty title="No tasks yet">Anything you promised them goes here, so it lands on the Work page too.</Empty>
      ) : (
        <ul className="mt-3 space-y-1">
          {[...open, ...done].map((t) => {
            const due = t.due_at ? dueLabel(t.due_at) : null;
            const isDone = t.status === "completed";
            return (
              <li key={t.id} className="flex items-start gap-2.5 rounded-lg px-1 py-1.5">
                <span className="mt-0.5"><TickButton done={isDone} onToggle={() => void toggle(t)} /></span>
                <div className="min-w-0 flex-1">
                  <div className={cn("text-[13px]", isDone ? "text-zinc-500 line-through" : "text-zinc-200")}>{t.title}</div>
                  <div className="flex flex-wrap gap-x-2 text-[11px] text-zinc-500">
                    {due && !isDone && <span className={TONE_CLASS[due.tone]}>{due.text}</span>}
                    {t.assignee_name && <span>{memberName(t.assignee_name).split(/\s+/)[0]}</span>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
